import axios from 'axios';
import merge from 'deepmerge';
import track from 'react-tracking';
import log from 'chameleon/ui-stack/utilities/log';
import { isImpersonating } from 'legacy/src/components/impersonating/utils';
import dayjs from 'dayjs';

/**
 * @constant
 * @type {object}
 * @default
 */
const envMapping = {
  dev: 'development',
  testing: 'testing',
  staging: 'staging',
  integration: 'integration',
  optesting: 'testing',
  p2testing: 'testing',
  perf: 'performance',
  prod: 'production',
  uat: 'uat',
};

export const _sendToTelemetry = (data, blockImpersonating) => {
  /**
   * If we're in impersonation mode, don't send
   * telemetry data
   */
  if (blockImpersonating) {
    return Promise.resolve(true);
  }
  // Mapping to conform API schema
  const post_data = merge(data, {
    params: {
      object: data.name || window.location.pathname,
    },
  });
  /**
   * If the request fails and catch() is invoked, capture the exception and return
   * a resolved promise to continue the promise chain (if applicable).
   */
  return axios({
    url: `${FYM_TELEMETRY_BACKEND_URL}`,
    method: 'post',
    headers: {
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(post_data),
  }).catch((error) => {
    log('error', error);

    return Promise.resolve(true);
  });
};

export const sendImpersonatingDetailsToTelemetry = (data) => {
  _sendToTelemetry(data, false);
};
/**
 * @constant
 * @type {Object}
 * @default
 */
export const sendToTelemetry = (data) =>
  _sendToTelemetry(data, isImpersonating());

/**
 * Default values that will be send to the Telemetry API
 * @constant
 * @type {array}
 * @default
 */
export const TLTRY_DEFAULTS = [
  // Default tracking data
  {
    env: envMapping[process.env.ENV] || 'development',
    v: 1,
  },
  // Default options
  {
    dispatch: _sendToTelemetry,
  },
];

export const TELEMETRY_EVENTS = {
  teacherAssign: 'spp_teacher_assigned',
  teacherUpdate: 'spp_teacher_updated',
  teacherMarkComplete: 'spp_teacher_mark_complete',
  teacherReopen: 'spp_teacher_reopen',
  sppTeacherDeleted: 'spp_teacher_assignment_deleted',
  teacherDeleted: 'teacher_assignment_deleted',
  sppPartAttempted: 'spp_part_attempted',
  sppHintAttempted: 'spp_hint_attempted',
  sppStartSelfAssigned: 'spp_start_self_assigned',
  sppStartTeacherAssigned: 'spp_start_teacher_assigned',
  sppSavedAndExited: 'spp_saved_and_exited',
  sppSubmitted: 'spp_submitted',
  sppUnansweredPartClick: 'spp_unanswered_part_click',
  sppReviewOpened: 'spp_review_opened',
  teacherSubmitStudentAssignment: 'teacher_submits_assignment',
  teacherUnSubmitStudentAssignment: 'teacher_unsubmits_assignment',
  sppDisabledRevealTipClick: 'spp_disabled_reveal_tip_click',
  sppDisabledRevealAnswerTipClick: 'spp_disabled_reveal_answer_tip_click',
  teacherUpdateShowLabels: 'teacher_update_show_labels',
  openSourceResource: 'open_source_resource',
  openJsTorResource: 'open_jstor_resource',
  casImplicitDelete: 'cas_implicit_delete',
  ackJsTorTerms: 'acknowledge_jstor_terms',
};

/**
 * Telemetry function decorates track function providing
 * default values, without compromising functionality.
 *
 * @param {*} args Arguments for the track function
 * @returns {function} Wrapped track function with defaults
 */
export default function telemetry(...args) {
  const opts = merge(TLTRY_DEFAULTS[1], args[1] || {});
  let data = merge(TLTRY_DEFAULTS[0], args[0] || {});

  if (typeof args[0] === 'function') {
    data = (...a) => {
      // When fn returns a falsy value return an empty object
      let res = args[0](...a) || {};

      return merge(TLTRY_DEFAULTS[0], res);
    };
  }

  return (...toDecorate) => track(data, opts)(...toDecorate);
}

/**
 * Send telemetry event with metadata
 *
 * @param {*} app
 * @param {*} eventType
 * @param {*} context additional telemetry data
 */
export const handleTelemetry = (app, eventType, context) => {
  const { importId: cbUserId, initId: amUserId } = window.current_user || {};
  const telemetryData = {
    app: app,
    event_type: eventType,
    date_clicked: dayjs.utc().format(),
    context: {
      ...context,
    },
    user: {
      am_user_id: amUserId,
      cb_user_id: cbUserId,
    },
    ...TLTRY_DEFAULTS[0],
  };
  sendToTelemetry(telemetryData);
};
